import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/Layout";
import ExplainabilityViewer from "@/components/ExplainabilityViewer";
import { toast } from "sonner";
import { Send, Bot, User, BookOpen, Brain, Plus, MessageSquare, ChevronRight, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Chat({ user, cabinet, onLogout }) {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(conversationId || null);
  const [conversations, setConversations] = useState([]);
  const [explainabilityOpen, setExplainabilityOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversation list on mount
  useEffect(() => {
    loadConversations();
  }, []);

  // Load messages when conversationId param changes
  useEffect(() => {
    if (conversationId) {
      loadMessages(conversationId);
      setCurrentConversationId(conversationId);
    } else {
      setMessages([]);
      setCurrentConversationId(null);
    }
  }, [conversationId]);

  const loadConversations = async () => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      const response = await axios.get(`${API}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data || []);
    } catch {
      // silently fail
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadMessages = async (convId) => {
    try {
      const token = localStorage.getItem('lawliet_token');
      const response = await axios.get(`${API}/conversations/${convId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch {
      toast.error("Erreur lors du chargement des messages");
    }
  };

  const startNewConversation = () => {
    navigate('/chat');
    setMessages([]);
    setCurrentConversationId(null);
  };

  const selectConversation = (convId) => {
    navigate(`/chat/${convId}`);
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = input;
    setInput("");
    setLoading(true);

    const tempUserMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMessage]);

    // P0.1 — Streaming placeholder for assistant message
    const streamingId = `streaming-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: streamingId,
      role: "assistant",
      content: "",
      timestamp: new Date().toISOString(),
      _streaming: true
    }]);

    try {
      const token = localStorage.getItem('lawliet_token');

      // Use fetch with ReadableStream for SSE
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",   // P0.5 — send httpOnly cookie too
        body: JSON.stringify({
          conversation_id: currentConversationId,
          message: userMessage
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = "";
      let finalConvId = null;
      let finalCitations = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const dataStr = line.slice(6).trim();
          if (!dataStr) continue;

          try {
            const event = JSON.parse(dataStr);

            if (event.type === "token") {
              fullContent += event.content;
              // Update streaming message in real time
              setMessages(prev => prev.map(m =>
                m.id === streamingId
                  ? { ...m, content: fullContent }
                  : m
              ));
            } else if (event.type === "done") {
              finalConvId = event.conversation_id;
              finalCitations = event.citations || [];
              // Finalize message
              setMessages(prev => prev.map(m =>
                m.id === streamingId
                  ? { ...m, id: event.message_id || streamingId, content: fullContent, citations: finalCitations, _streaming: false }
                  : m
              ));
              if (!currentConversationId && finalConvId) {
                setCurrentConversationId(finalConvId);
                navigate(`/chat/${finalConvId}`, { replace: true });
                loadConversations();
              }
            } else if (event.type === "error") {
              throw new Error(event.detail || "Streaming error");
            }
          } catch (parseErr) {
            // Skip malformed SSE lines
          }
        }
      }

    } catch (error) {
      // Remove the streaming placeholder on error
      setMessages(prev => prev.filter(m => m.id !== streamingId));
      toast.error(error.message || "Erreur lors de l'envoi du message");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatConvDate = (dateStr) => {
    const date = new Date(dateStr);
    const diff = Math.floor((Date.now() - date) / 86400000);
    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return "Hier";
    if (diff < 7) return `Il y a ${diff}j`;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  const getConvTitle = (conv) => {
    return conv.title || "Nouvelle conversation";
  };

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="h-[calc(100vh-130px)] flex gap-4" data-testid="chat-interface">
        {/* Left sidebar: Conversation History */}
        <div className="w-64 flex-shrink-0 flex flex-col">
          <Button
            onClick={startNewConversation}
            className="w-full mb-3 bg-blue-600 hover:bg-blue-700 gap-2"
            data-testid="new-conversation-btn"
          >
            <Plus className="h-4 w-4" />
            Nouvelle conversation
          </Button>

          <Card className="flex-1 overflow-hidden">
            <div className="p-3 border-b">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Historique</p>
            </div>
            <ScrollArea className="flex-1 h-[calc(100%-44px)]">
              {historyLoading ? (
                <div className="p-3 space-y-2">
                  {[1,2,3].map(i => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}
                </div>
              ) : conversations.length === 0 ? (
                <div className="p-4 text-center text-slate-400">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p className="text-xs">Aucune conversation</p>
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {conversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => selectConversation(conv.id)}
                      data-testid={`conv-item-${conv.id}`}
                      className={`w-full text-left p-2.5 rounded-lg transition-colors group ${
                        currentConversationId === conv.id
                          ? 'bg-blue-600 text-white'
                          : 'hover:bg-slate-100 text-slate-700'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-1">
                        <p className={`text-xs font-medium truncate flex-1 ${
                          currentConversationId === conv.id ? 'text-white' : 'text-slate-800'
                        }`}>
                          {getConvTitle(conv)}
                        </p>
                      </div>
                      <p className={`text-xs mt-0.5 ${
                        currentConversationId === conv.id ? 'text-blue-200' : 'text-slate-400'
                      }`}>
                        {formatConvDate(conv.updated_at || conv.created_at)}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </Card>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="mb-3">
            <h1 className="text-2xl font-bold text-blue-900" style={{ fontFamily: 'EB Garamond, serif' }}>
              Assistant Juridique
            </h1>
            <p className="text-slate-500 text-sm">Questions sur le droit français et européen, avec citations vérifiées</p>
          </div>

          <Card className="flex-1 flex flex-col overflow-hidden">
            <ScrollArea className="flex-1 p-6">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center space-y-4 py-12" data-testid="empty-chat-state">
                  <div className="bg-blue-100 p-6 rounded-full">
                    <Bot className="h-12 w-12 text-blue-900" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-blue-900 mb-2">Comment puis-je vous aider ?</h3>
                    <p className="text-slate-500 max-w-md text-sm">
                      Droit des contrats, RGPD, jurisprudence française et européenne, analyse de clauses...
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 max-w-md w-full mt-4">
                    {[
                      "Quelles sont les conditions de validité d'un contrat ?",
                      "Expliquez l'article 1218 du Code civil",
                      "Qu'est-ce que le droit à la déconnexion ?",
                      "Quelles sont les sanctions RGPD en cas de violation ?"
                    ].map((q) => (
                      <button
                        key={q}
                        onClick={() => { setInput(q); }}
                        className="text-left p-3 text-xs bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded-lg transition text-slate-700"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {messages.map((message, index) => (
                    <div
                      key={message.id || index}
                      className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                      data-testid={`message-${message.role}`}
                    >
                      <div className={`flex items-start space-x-3 max-w-3xl ${
                        message.role === "user" ? "flex-row-reverse space-x-reverse" : ""
                      }`}>
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          message.role === "user" ? "bg-slate-200" : "bg-blue-600"
                        }`}>
                          {message.role === "user" ? (
                            <User className="h-4 w-4 text-slate-700" />
                          ) : (
                            <Bot className="h-4 w-4 text-white" />
                          )}
                        </div>

                        <div className="flex-1">
                          <Card className={message.role === "user" ? "bg-slate-100" : "bg-blue-50 border-blue-200"}>
                            <CardContent className="p-4">
                              {message.role === "user" ? (
                                <p className="text-slate-800 whitespace-pre-wrap">
                                  {message.content}
                                </p>
                              ) : (
                                <div className="chat-ai-bubble">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {message.content}
                                  </ReactMarkdown>
                                  {message._streaming && (
                                    <span className="inline-block w-0.5 h-4 bg-blue-700 ml-0.5 animate-pulse align-middle" />
                                  )}
                                </div>
                              )}

                              {message.citations && message.citations.length > 0 && (
                                <div className="chat-citation-block mt-4 space-y-2">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2 text-sm font-semibold text-blue-900">
                                      <BookOpen className="h-4 w-4" />
                                      <span>Sources et citations :</span>
                                    </div>
                                    {message.metadata?.confidence && (
                                      <div className="flex items-center space-x-2">
                                        <span className="text-xs text-slate-500">Confiance:</span>
                                        <Badge variant="outline" className="text-xs">
                                          {(message.metadata.confidence * 100).toFixed(0)}%
                                        </Badge>
                                      </div>
                                    )}
                                  </div>
                                  {message.citations.map((citation, idx) => (
                                    <div key={idx} className="bg-white p-3 rounded-lg border border-blue-200">
                                      <div className="flex items-center space-x-2 mb-1">
                                        <Badge variant="outline" className="text-xs">{citation.source}</Badge>
                                        {citation.article && (
                                          <Badge variant="secondary" className="text-xs">{citation.article}</Badge>
                                        )}
                                        {citation.confidence && (
                                          <Badge className="text-xs bg-green-100 text-green-800 border-green-200">
                                            {(citation.confidence * 100).toFixed(0)}%
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-sm text-slate-600 italic">"{citation.text}"</p>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {message.role === "assistant" && (
                                <div className="mt-3">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedMessage(message);
                                      setExplainabilityOpen(true);
                                    }}
                                    className="text-blue-900 border-blue-200 hover:bg-blue-50 text-xs"
                                  >
                                    <Brain className="h-3 w-3 mr-1" />
                                    Expliquer le raisonnement
                                  </Button>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Input area */}
            <div className="border-t p-4 bg-white">
              <div className="flex space-x-2">
                <Input
                  data-testid="chat-input"
                  placeholder="Posez votre question juridique..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  className="flex-1"
                />
                <Button
                  data-testid="send-message-button"
                  onClick={handleSend}
                  disabled={loading || !input.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {selectedMessage && (
        <ExplainabilityViewer
          query={messages.find(m => m.role === "user" && messages.indexOf(m) < messages.indexOf(selectedMessage))?.content || "Question"}
          response={selectedMessage.content}
          context={{
            citations: selectedMessage.citations,
            confidence: selectedMessage.metadata?.confidence
          }}
          isOpen={explainabilityOpen}
          onClose={() => {
            setExplainabilityOpen(false);
            setSelectedMessage(null);
          }}
        />
      )}
    </Layout>
  );
}
